// CODA-970: View and manage an existing onboarding request.
// CODA-1944: 2-phase portal — two link sections in the Portal-Link tab.
import { useState, useEffect, useCallback } from 'react'
import { isAxiosError } from 'axios'
import apiClient from '@/lib/api'
import {
  Modal,
  Button,
  Input,
  Textarea,
  Switch,
  Spinner,
  ErrorState,
  LoadingState,
  StatusBadge,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  useToast,
} from '@/components/ui'
import {
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Plus,
  Trash2,
  FileText,
  Send,
  Copy,
  Building2,
  User,
  Lock,
} from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

interface WizardDoc {
  id: string
  title: string
  description: string | null
  file_name: string | null
  requires_signature: boolean
  sort_order: number
}

interface TokenInfo {
  expires_at: string
  sent_at: string | null
}

interface TokenResult {
  token: string
  expiresAt: string
  portalUrl: string
  emailSent: boolean
  phase: 'phase1' | 'phase2'
}

interface OnboardingDetail {
  id: string
  status: 'draft' | 'sent' | 'in_progress' | 'completed' | 'expired'
  company_name: string
  company_street: string | null
  company_zip: string | null
  company_city: string | null
  company_country: string | null
  contact_first_name: string | null
  contact_last_name: string | null
  contact_email: string
  contact_phone: string | null
  notes: string | null
  sap_card_code: string | null
  sap_sync_status: 'pending' | 'success' | 'error'
  sap_sync_error: string | null
  token_ttl_hours: number
  created_at: string
  phase1_completed_at: string | null
  completed_at: string | null
  documents: WizardDoc[]
  token: TokenInfo | null        // phase2 token
  phase1Token: TokenInfo | null  // phase1 token
}

const STATUS_LABEL: Record<OnboardingDetail['status'], string> = {
  draft: 'Entwurf',
  sent: 'Versendet',
  in_progress: 'In Bearbeitung',
  completed: 'Abgeschlossen',
  expired: 'Abgelaufen',
}

interface Props {
  requestId: string
  onClose: () => void
  onUpdated: () => void
}

export function OnboardingDetailModal({ requestId, onClose, onUpdated }: Props) {
  const { toast } = useToast()
  const [detail, setDetail] = useState<OnboardingDetail | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  // SAP retry state
  const [sapRetrying, setSapRetrying] = useState(false)
  const [sapRetryError, setSapRetryError] = useState<string | null>(null)

  // Documents state
  const [docTitle, setDocTitle] = useState('')
  const [docDesc, setDocDesc] = useState('')
  const [docSig, setDocSig] = useState(false)
  const [docLoading, setDocLoading] = useState(false)

  // Phase-1 token state
  const [phase1TtlHours, setPhase1TtlHours] = useState(720)
  const [phase1TokenResult, setPhase1TokenResult] = useState<TokenResult | null>(null)
  const [phase1TokenLoading, setPhase1TokenLoading] = useState(false)
  const [phase1TokenError, setPhase1TokenError] = useState<string | null>(null)

  // Phase-2 token state
  const [phase2TtlHours, setPhase2TtlHours] = useState(720)
  const [phase2TokenResult, setPhase2TokenResult] = useState<TokenResult | null>(null)
  const [phase2TokenLoading, setPhase2TokenLoading] = useState(false)
  const [phase2TokenError, setPhase2TokenError] = useState<string | null>(null)

  const loadDetail = useCallback(async () => {
    setLoadError(null)
    try {
      const { data } = await apiClient.get<OnboardingDetail>(`/onboarding/${requestId}`)
      setDetail(data)
      if (data.token_ttl_hours) {
        setPhase1TtlHours(data.token_ttl_hours)
        setPhase2TtlHours(data.token_ttl_hours)
      }
    } catch (err) {
      const msg = isAxiosError(err)
        ? (err.response?.data?.error ?? err.message)
        : 'Unbekannter Fehler'
      setLoadError(msg)
    }
  }, [requestId])

  useEffect(() => { loadDetail() }, [loadDetail])

  async function retrySap() {
    setSapRetrying(true)
    setSapRetryError(null)
    try {
      await apiClient.post(`/onboarding/${requestId}/sap-retry`, {})
      await loadDetail()
      onUpdated()
      toast({ title: 'SAP Business Partner angelegt', tone: 'success' })
    } catch (err) {
      const msg = isAxiosError(err)
        ? (err.response?.data?.sapError ?? err.response?.data?.error ?? err.message)
        : 'Unbekannter Fehler'
      setSapRetryError(msg)
      await loadDetail()
    } finally {
      setSapRetrying(false)
    }
  }

  async function addDoc() {
    if (!docTitle.trim()) return
    setDocLoading(true)
    try {
      await apiClient.post(`/onboarding/${requestId}/documents`, {
        title: docTitle.trim(),
        description: docDesc.trim() || null,
        requiresSignature: docSig,
      })
      setDocTitle('')
      setDocDesc('')
      setDocSig(false)
      await loadDetail()
    } catch {
      toast({ title: 'Fehler', description: 'Dokument konnte nicht hinzugefügt werden.', tone: 'danger' })
    } finally {
      setDocLoading(false)
    }
  }

  async function deleteDoc(docId: string) {
    try {
      await apiClient.delete(`/onboarding/${requestId}/documents/${docId}`)
      await loadDetail()
    } catch {
      toast({ title: 'Fehler', description: 'Dokument konnte nicht gelöscht werden.', tone: 'danger' })
    }
  }

  async function sendPhase1Token() {
    setPhase1TokenLoading(true)
    setPhase1TokenError(null)
    try {
      const { data } = await apiClient.post<TokenResult>(`/onboarding/${requestId}/token`, {
        tokenTtlHours: phase1TtlHours,
        phase: 'phase1',
      })
      setPhase1TokenResult(data)
      await loadDetail()
      onUpdated()
    } catch (err) {
      const msg = isAxiosError(err)
        ? (err.response?.data?.error ?? err.message)
        : 'Unbekannter Fehler'
      setPhase1TokenError(msg)
    } finally {
      setPhase1TokenLoading(false)
    }
  }

  async function sendPhase2Token() {
    setPhase2TokenLoading(true)
    setPhase2TokenError(null)
    try {
      const { data } = await apiClient.post<TokenResult>(`/onboarding/${requestId}/token`, {
        tokenTtlHours: phase2TtlHours,
        phase: 'phase2',
      })
      setPhase2TokenResult(data)
      await loadDetail()
      onUpdated()
    } catch (err) {
      const msg = isAxiosError(err)
        ? (err.response?.data?.error ?? err.message)
        : 'Unbekannter Fehler'
      setPhase2TokenError(msg)
    } finally {
      setPhase2TokenLoading(false)
    }
  }

  const title = detail
    ? `Onboarding: ${detail.company_name}`
    : 'Onboarding laden…'

  return (
    <Modal open onClose={onClose} title={title} size="lg">
      {!detail && !loadError && <LoadingState label="Lade Onboarding-Daten…" />}

      {loadError && (
        <ErrorState
          title="Fehler beim Laden"
          description={loadError}
          onRetry={loadDetail}
        />
      )}

      {detail && (
        <Tabs defaultValue="overview">
          <TabsList className="mb-4">
            <TabsTrigger value="overview">Übersicht</TabsTrigger>
            <TabsTrigger value="documents">Dokumente ({detail.documents.length})</TabsTrigger>
            <TabsTrigger value="link">Portal-Links</TabsTrigger>
          </TabsList>

          {/* ── Overview ────────────────────────────────────────────────────── */}
          <TabsContent value="overview">
            <div className="space-y-5">
              <div className="flex items-center gap-2">
                <StatusBadge status={detail.status} label={STATUS_LABEL[detail.status]} />
                <span className="text-xs text-gray-400">
                  Erstellt: {new Date(detail.created_at).toLocaleDateString('de-DE')}
                </span>
                {detail.phase1_completed_at && (
                  <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Phase 1 abgeschlossen
                  </span>
                )}
              </div>

              {/* Company */}
              <div className="rounded-lg border border-gray-200 p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Building2 className="h-4 w-4 text-gray-400" />
                  Firmendaten
                </div>
                <Row label="Firma" value={detail.company_name} />
                {detail.company_street && (
                  <Row label="Adresse" value={[detail.company_street, `${detail.company_zip ?? ''} ${detail.company_city ?? ''}`.trim()].filter(Boolean).join(', ')} />
                )}
                {detail.company_country && <Row label="Land" value={detail.company_country} />}
                {detail.notes && <Row label="Notiz" value={detail.notes} />}
              </div>

              {/* Contact */}
              <div className="rounded-lg border border-gray-200 p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <User className="h-4 w-4 text-gray-400" />
                  Ansprechpartner
                </div>
                {(detail.contact_first_name || detail.contact_last_name) && (
                  <Row label="Name" value={[detail.contact_first_name, detail.contact_last_name].filter(Boolean).join(' ')} />
                )}
                <Row label="E-Mail" value={detail.contact_email} />
                {detail.contact_phone && <Row label="Telefon" value={detail.contact_phone} />}
              </div>

              {/* SAP Status */}
              <div className="rounded-lg border border-gray-200 p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">SAP Business Partner</span>
                  {detail.sap_sync_status === 'success' && detail.sap_card_code && (
                    <span className="flex items-center gap-1.5 text-sm text-green-700">
                      <CheckCircle className="h-4 w-4" />
                      CardCode: <span className="font-mono font-semibold">{detail.sap_card_code}</span>
                    </span>
                  )}
                  {detail.sap_sync_status === 'error' && (
                    <span className="flex items-center gap-1.5 text-sm text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      Fehler
                    </span>
                  )}
                  {detail.sap_sync_status === 'pending' && (
                    <span className="text-sm text-gray-400">Ausstehend</span>
                  )}
                </div>

                {detail.sap_sync_status === 'error' && (
                  <div className="space-y-2 mt-2">
                    {detail.sap_sync_error && (
                      <p className="text-xs text-red-600 bg-red-50 rounded p-2 font-mono break-all">
                        {detail.sap_sync_error}
                      </p>
                    )}
                    {sapRetryError && (
                      <p className="text-xs text-red-600 bg-red-50 rounded p-2 font-mono break-all">
                        {sapRetryError}
                      </p>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={sapRetrying}
                      onClick={retrySap}
                    >
                      {sapRetrying ? <Spinner size="sm" className="mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                      SAP Business Partner erneut anlegen
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ── Dokumente ────────────────────────────────────────────────────── */}
          <TabsContent value="documents">
            <div className="space-y-4">
              {detail.documents.length > 0 && (
                <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
                  {detail.documents.map((doc) => (
                    <li key={doc.id} className="flex items-center justify-between px-4 py-3 bg-white">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="h-4 w-4 text-gray-400 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{doc.title}</p>
                          {doc.description && (
                            <p className="text-xs text-gray-400 truncate">{doc.description}</p>
                          )}
                          {doc.requires_signature && (
                            <span className="text-xs text-amber-600 font-medium">Unterschrift erforderlich</span>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => deleteDoc(doc.id)}
                        aria-label="Dokument entfernen"
                      >
                        <Trash2 className="h-4 w-4 text-red-400" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="border border-dashed border-gray-300 rounded-lg p-4 space-y-3">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Neues Dokument</p>
                <Input
                  label="Titel *"
                  value={docTitle}
                  onChange={(e) => setDocTitle(e.target.value)}
                  placeholder="z.B. Allgemeine Geschäftsbedingungen"
                />
                <Textarea
                  label="Beschreibung"
                  value={docDesc}
                  onChange={(e) => setDocDesc(e.target.value)}
                  placeholder="Kurze Beschreibung…"
                  rows={2}
                />
                <Switch
                  label="Unterschrift erforderlich"
                  checked={docSig}
                  onChange={(e) => setDocSig(e.target.checked)}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!docTitle.trim() || docLoading}
                  onClick={addDoc}
                >
                  {docLoading ? <Spinner size="sm" className="mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  Dokument hinzufügen
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* ── Portal-Links (Phase 1 + Phase 2) ────────────────────────────── */}
          <TabsContent value="link">
            <div className="space-y-6">

              {/* ── Phase 1 ──────────────────────────────────────────────────── */}
              <div className="rounded-lg border border-blue-200 p-4 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-blue-800">Phase 1 – Stammdaten-Bestätigung</h3>
                    <p className="text-xs text-blue-600 mt-0.5">
                      Kunde prüft und bestätigt Unternehmensstammdaten und Ansprechpartner.
                    </p>
                  </div>
                  {detail.phase1_completed_at && (
                    <span className="flex items-center gap-1 text-xs text-green-700 font-medium shrink-0 ml-2">
                      <CheckCircle className="h-3.5 w-3.5" />
                      Abgeschlossen {new Date(detail.phase1_completed_at).toLocaleDateString('de-DE')}
                    </span>
                  )}
                </div>

                {detail.phase1Token && (
                  <div className="rounded bg-blue-50 border border-blue-100 p-2 text-xs text-blue-700">
                    Zuletzt versendet: {detail.phase1Token.sent_at
                      ? new Date(detail.phase1Token.sent_at).toLocaleString('de-DE')
                      : '–'
                    } · Gültig bis: {new Date(detail.phase1Token.expires_at).toLocaleDateString('de-DE')}
                  </div>
                )}

                {!phase1TokenResult ? (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-500">
                      Generiere einen Phase-1-Link für <strong>{detail.contact_email}</strong>.
                    </p>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Link-Gültigkeit (Stunden)</label>
                      <select
                        className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                        value={phase1TtlHours}
                        onChange={(e) => setPhase1TtlHours(Number(e.target.value))}
                      >
                        <option value={72}>72 Stunden (3 Tage)</option>
                        <option value={168}>168 Stunden (1 Woche)</option>
                        <option value={336}>336 Stunden (2 Wochen)</option>
                        <option value={720}>720 Stunden (30 Tage)</option>
                        <option value={2160}>2160 Stunden (90 Tage)</option>
                      </select>
                    </div>

                    {phase1TokenError && (
                      <ErrorState title="Link konnte nicht generiert werden" description={phase1TokenError} />
                    )}

                    <Button
                      variant="secondary"
                      disabled={phase1TokenLoading}
                      onClick={sendPhase1Token}
                      className="w-full"
                    >
                      {phase1TokenLoading ? <Spinner size="sm" className="mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                      Phase-1-Link generieren &amp; versenden
                    </Button>
                  </div>
                ) : (
                  <PhaseTokenSuccess result={phase1TokenResult} onReset={() => setPhase1TokenResult(null)} />
                )}
              </div>

              {/* ── Phase 2 ──────────────────────────────────────────────────── */}
              <div className={`rounded-lg border p-4 space-y-4 ${detail.phase1_completed_at ? 'border-gray-200' : 'border-gray-100 bg-gray-50 opacity-75'}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className={`text-sm font-semibold ${detail.phase1_completed_at ? 'text-gray-800' : 'text-gray-400'}`}>
                      Phase 2 – Vertragsdokumente
                    </h3>
                    <p className={`text-xs mt-0.5 ${detail.phase1_completed_at ? 'text-gray-500' : 'text-gray-400'}`}>
                      Kunde liest und bestätigt Vertragsdokumente und lädt ggf. Unterschrift hoch.
                    </p>
                  </div>
                  {!detail.phase1_completed_at && (
                    <span className="flex items-center gap-1 text-xs text-gray-400 shrink-0 ml-2">
                      <Lock className="h-3.5 w-3.5" />
                      Wartet auf Phase 1
                    </span>
                  )}
                  {detail.completed_at && (
                    <span className="flex items-center gap-1 text-xs text-green-700 font-medium shrink-0 ml-2">
                      <CheckCircle className="h-3.5 w-3.5" />
                      Onboarding abgeschlossen
                    </span>
                  )}
                </div>

                {!detail.phase1_completed_at && (
                  <p className="text-sm text-gray-400 italic">
                    Phase-2-Link kann erst versendet werden, wenn der Kunde Phase 1 abgeschlossen hat.
                  </p>
                )}

                {detail.phase1_completed_at && (
                  <>
                    {detail.token && (
                      <div className="rounded bg-gray-50 border border-gray-100 p-2 text-xs text-gray-600">
                        Zuletzt versendet: {detail.token.sent_at
                          ? new Date(detail.token.sent_at).toLocaleString('de-DE')
                          : '–'
                        } · Gültig bis: {new Date(detail.token.expires_at).toLocaleDateString('de-DE')}
                      </div>
                    )}

                    {!phase2TokenResult ? (
                      <div className="space-y-3">
                        <p className="text-sm text-gray-500">
                          Generiere einen Phase-2-Link für <strong>{detail.contact_email}</strong>.
                          {detail.documents.length === 0 && (
                            <span className="block text-amber-600 mt-1">
                              Hinweis: Noch keine Dokumente hinterlegt. Bitte zuerst Dokumente im Tab „Dokumente" hinzufügen.
                            </span>
                          )}
                        </p>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Link-Gültigkeit (Stunden)</label>
                          <select
                            className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                            value={phase2TtlHours}
                            onChange={(e) => setPhase2TtlHours(Number(e.target.value))}
                          >
                            <option value={72}>72 Stunden (3 Tage)</option>
                            <option value={168}>168 Stunden (1 Woche)</option>
                            <option value={336}>336 Stunden (2 Wochen)</option>
                            <option value={720}>720 Stunden (30 Tage)</option>
                            <option value={2160}>2160 Stunden (90 Tage)</option>
                          </select>
                        </div>

                        {phase2TokenError && (
                          <ErrorState title="Link konnte nicht generiert werden" description={phase2TokenError} />
                        )}

                        <Button
                          variant="primary"
                          disabled={phase2TokenLoading}
                          onClick={sendPhase2Token}
                          className="w-full"
                        >
                          {phase2TokenLoading ? <Spinner size="sm" className="mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                          Phase-2-Link generieren &amp; versenden
                        </Button>
                      </div>
                    ) : (
                      <PhaseTokenSuccess result={phase2TokenResult} onReset={() => setPhase2TokenResult(null)} />
                    )}
                  </>
                )}
              </div>

            </div>
          </TabsContent>
        </Tabs>
      )}

      <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
        <Button variant="ghost" onClick={onClose}>Schließen</Button>
      </div>
    </Modal>
  )
}

// ─── Shared success UI for a generated token ─────────────────────────────────

function PhaseTokenSuccess({ result, onReset }: { result: TokenResult; onReset: () => void }) {
  const { toast } = useToast()
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
        <CheckCircle className="h-4 w-4" />
        {result.emailSent
          ? 'Link generiert und E-Mail versendet!'
          : 'Link generiert (E-Mail fehlgeschlagen – bitte manuell senden)'}
      </div>
      {!result.emailSent && (
        <p className="text-xs text-amber-600">Kopiere den Link unten und sende ihn manuell.</p>
      )}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center gap-2">
        <span className="text-xs text-gray-600 font-mono break-all flex-1 min-w-0">{result.portalUrl}</span>
        <Button
          variant="ghost"
          size="xs"
          aria-label="Link kopieren"
          onClick={() => {
            navigator.clipboard.writeText(result.portalUrl)
            toast({ title: 'Link kopiert' })
          }}
        >
          <Copy className="h-4 w-4" />
        </Button>
      </div>
      <Button variant="secondary" size="sm" className="w-full" onClick={onReset}>
        Neuen Link generieren
      </Button>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-gray-400 shrink-0 w-24">{label}</span>
      <span className="text-gray-800">{value}</span>
    </div>
  )
}
